<template>
  <div class="pb-wrap" v-if="provider !== null">
    <div class="pb-head">
      <h3>GPS benchmarks</h3>
      <div class="pb-filters">
        <select v-model="sessionType" @change="load">
          <option value="training">Training</option>
          <option value="game">Games</option>
          <option value="">Both</option>
        </select>
        <select v-model="mdOffset" @change="load">
          <option value="all">All match days</option>
          <option v-for="n in mdRange" :key="n" :value="n">{{ mdLabel(n) }}</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="pb-msg">Loading…</div>
    <div v-else-if="error" class="pb-msg pb-error">{{ error }}</div>
    <div v-else-if="!sessions" class="pb-msg">
      No GPS sessions recorded for this player{{ filterWords }}.
    </div>
    <div v-else>
      <p class="pb-meta">
        {{ sessions }} session{{ sessions === 1 ? "" : "s" }}{{ filterWords }}
        <template v-if="latestDate"> · most recent {{ formatDate(latestDate) }}</template>
      </p>

      <!-- Below three sessions a "personal best" is one good day, not a
           ceiling, so the comparison is withheld rather than shown wrong. -->
      <p v-if="!enoughData" class="pb-thin">
        Showing the latest session only — a benchmark needs at least three
        comparable sessions before bests and squad averages mean anything.
      </p>

      <div class="pb-grid">
        <div v-for="(m, key) in metrics" :key="key" class="pb-tile">
          <span class="pb-label">{{ m.label }}</span>
          <span class="pb-latest">
            {{ fmt(m.latest, key) }}<small v-if="m.unit"> {{ m.unit }}</small>
          </span>

          <template v-if="enoughData && m.best">
            <div class="pb-bar" :title="`${m.pct_of_best}% of personal best`">
              <span
                class="pb-fill"
                :style="{ width: Math.min(100, m.pct_of_best || 0) + '%', background: tone(m) }"
              ></span>
            </div>
            <span class="pb-sub">
              {{ m.pct_of_best }}% of best ({{ fmt(m.best, key) }})
            </span>
            <span class="pb-sub pb-squad" v-if="m.squad_avg">
              squad {{ fmt(m.squad_avg, key) }}
              <em :class="vsSquad(m).cls">{{ vsSquad(m).text }}</em>
            </span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { PlayerData } from "@/services/playerData-service.js";

export default {
  name: "GpsPlayerBenchmark",
  props: { playerId: { type: [Number, String], required: true } },
  data() {
    return {
      loading: false,
      error: null,
      provider: null,
      sessions: 0,
      enoughData: false,
      latestDate: null,
      metrics: {},
      sessionType: "training",
      mdOffset: "all",
      mdRange: [-5, -4, -3, -2, -1, 0],
    };
  },
  watch: {
    playerId: "load",
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      if (!this.playerId) return;
      this.loading = true;
      this.error = null;
      try {
        const { data } = await PlayerData.find({
          action: "getPlayerGpsBenchmarks",
          playerId: this.playerId,
          sessionType: this.sessionType,
          mdOffset: this.mdOffset,
        });
        const d = (data && data.data) || data || {};
        this.provider = d.provider ?? null;
        this.sessions = d.sessions || 0;
        this.enoughData = !!d.enough_data;
        this.latestDate = d.latest_date || null;
        this.metrics = d.metrics || {};
      } catch (e) {
        this.error = "Could not load GPS benchmarks.";
      } finally {
        this.loading = false;
      }
    },
    fmt(v, key) {
      if (v === null || v === undefined || v === "") return "—";
      const n = Number(v);
      if (key === "top_speed_kph") return n.toFixed(1);
      if (key === "total_distance_m" && n >= 1000) return (n / 1000).toFixed(2) + "K";
      return Math.round(n).toLocaleString();
    },
    tone(m) {
      const p = m.pct_of_best || 0;
      if (p >= 90) return "#198754";
      if (p >= 75) return "#7cb342";
      if (p >= 55) return "#f6aa1e";
      return "#dc3545";
    },
    vsSquad(m) {
      if (!m.squad_avg || m.latest == null) return { text: "", cls: "" };
      const diff = ((Number(m.latest) - Number(m.squad_avg)) / Number(m.squad_avg)) * 100;
      if (Math.abs(diff) < 5) return { text: "in line", cls: "pb-level" };
      return diff > 0
        ? { text: `+${Math.round(diff)}%`, cls: "pb-above" }
        : { text: `${Math.round(diff)}%`, cls: "pb-below" };
    },
    mdLabel(n) {
      return n === 0 ? "Match day" : `MD${n}`;
    },
    formatDate(d) {
      try {
        return new Date(String(d).replace(" ", "T")).toLocaleDateString("en-GB", {
          day: "numeric", month: "short", year: "numeric",
        });
      } catch (e) { return d; }
    },
  },
  computed: {
    filterWords() {
      const bits = [];
      if (this.sessionType) bits.push(this.sessionType === "game" ? "in games" : "in training");
      if (this.mdOffset !== "all") bits.push(`at ${this.mdLabel(Number(this.mdOffset))}`);
      return bits.length ? " " + bits.join(" ") : "";
    },
  },
};
</script>

<style scoped>
.pb-wrap { padding: 0.5rem 0 1.5rem; }
.pb-head { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.75rem; }
.pb-head h3 { font-size: 1rem; margin: 0; color: #212529; }
.pb-filters { display: flex; gap: 0.5rem; }
.pb-filters select { padding: 0.3rem 0.5rem; border: 1px solid #ced4da; border-radius: 6px; font-size: 0.8rem; }

.pb-msg { padding: 1rem 0; font-size: 0.87rem; color: #6c757d; }
.pb-error { color: #dc3545; }
.pb-meta { font-size: 0.78rem; color: #6c757d; margin: 0.4rem 0 0.9rem; }
.pb-thin { font-size: 0.78rem; color: #f6aa1e; margin: 0 0 0.9rem; }

.pb-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 0.85rem; }
.pb-tile { background: #f8f9fa; border-radius: 10px; padding: 0.8rem; display: flex; flex-direction: column; gap: 0.25rem; }
.pb-label { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; color: #868e96; }
.pb-latest { font-size: 1.3rem; font-weight: 700; color: #212529; line-height: 1.1; }
.pb-latest small { font-size: 0.7rem; font-weight: 500; color: #868e96; }
.pb-bar { height: 5px; background: #e9ecef; border-radius: 3px; margin-top: 0.35rem; overflow: hidden; }
.pb-fill { display: block; height: 100%; border-radius: 3px; }
.pb-sub { font-size: 0.7rem; color: #6c757d; }
.pb-squad em { font-style: normal; font-weight: 600; margin-left: 0.2rem; }
.pb-above { color: #198754; }
.pb-below { color: #dc3545; }
.pb-level { color: #868e96; }
</style>
